import { z } from 'zod';
import {
  INSTALLMENT_REPAYMENTS,
  PAYMENT_MODE_ACCOUNTING_EXPECTATIONS,
  PAYMENT_MODE_ADMIN_EXPECTATIONS,
  PAYMENT_MODE_CREDIT_HOLDERS,
  PaymentModeAdeCodeSchema,
} from './payment-modes';

/**
 * Task #4321 — Offerte di vendita in cassa per prodotto e split quote.
 *
 * L'unità di vendita in cassa è l'OFFERTA: una composizione del listino
 * (device + canvass + modalità di pagamento all'insegna con i numeri
 * ereditati), una tariffa canvas pura (listino canvas + modalità) oppure la
 * vendita semplice (riga di listino standard). La cassa sceglie l'offerta,
 * mai i numeri: anticipo, finanziato, rate, rata, canone ed entry fee sono
 * ereditati e in sola lettura; l'unico override ammesso è l'aliquota IVA.
 *
 * Piano INCASSO: le quote fisiche (anticipo device, entry fee canvass,
 * residuo non finanziato) si incassano sui metodi RT/fattura scelti in cassa;
 * la quota finanziata entra nel documento sul metodo del tenant DERIVATO dai
 * codici RT/fattura della modalità (`resolveFinancedQuotaMethod`), mai scelto
 * dal client.
 */

export const POS_OFFER_KINDS = ['plain', 'composition', 'canvas'] as const;
export type PosOfferKind = (typeof POS_OFFER_KINDS)[number];

/** Modalità di pagamento all'insegna ereditata dall'offerta. */
export const PosOfferPaymentModeSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  credit_holder: z.enum(PAYMENT_MODE_CREDIT_HOLDERS),
  accounting_expectation: z.enum(PAYMENT_MODE_ACCOUNTING_EXPECTATIONS),
  admin_expectation: z.enum(PAYMENT_MODE_ADMIN_EXPECTATIONS),
  /** Addebito effettivo della composizione (scelto tra gli ammessi dal template). */
  installment_repayment: z.enum(INSTALLMENT_REPAYMENTS),
  ade_code: PaymentModeAdeCodeSchema.nullable(),
  mp_code: z.string().nullable(),
});
export type PosOfferPaymentMode = z.infer<typeof PosOfferPaymentModeSchema>;

/** Tariffa canvass agganciata all'offerta (canone + entry fee effettivi). */
export const PosOfferCanvasSchema = z.object({
  canvas_item_id: z.string().uuid(),
  product_id: z.string().uuid(),
  product_name: z.string(),
  monthly_fee_effective: z.number().nullable(),
  entry_fee: z.number().nullable(),
  contract_duration_months: z.number().int().nullable(),
});
export type PosOfferCanvas = z.infer<typeof PosOfferCanvasSchema>;

export const PosOfferFinancingPartnerSchema = z.object({
  id: z.string().uuid(),
  legal_name: z.string(),
});
export type PosOfferFinancingPartner = z.infer<typeof PosOfferFinancingPartnerSchema>;

const PosOfferBaseSchema = z.object({
  price_list_id: z.string().uuid(),
  price_list_name: z.string(),
  list_type: z.string(),
  is_default: z.boolean(),
  is_customer_list: z.boolean(),
  /** Driver di business derivato del listino (canvas/bundle); null su base/promo. In cassa un Canvas nel carrello filtra i bundle sullo stesso driver. */
  business_driver_id: z.string().uuid().nullable(),
  /** Prezzo unitario LORDO già scontato (contratto canonico cassa). */
  unit_price_gross: z.number(),
  unit_price_net: z.number(),
  unit_vat_amount: z.number(),
  /** Prezzo di listino prima dello sconto (lordo). */
  list_price: z.number(),
  discount_percent: z.number(),
  vat_rate: z.number(),
  vat_rate_id: z.string().uuid().nullable(),
  vat_code: z.string().nullable(),
  max_discount_percent: z.number().nullable(),
});

/** Vendita semplice: riga di un listino standard, nessun credito. */
export const PosPlainOfferSchema = PosOfferBaseSchema.extend({
  kind: z.literal('plain'),
});
export type PosPlainOffer = z.infer<typeof PosPlainOfferSchema>;

/** Composizione (device + canvass + modalità) con i numeri ereditati dal listino. */
export const PosCompositionOfferSchema = PosOfferBaseSchema.extend({
  kind: z.literal('composition'),
  composition_id: z.string().uuid(),
  composition_name: z.string(),
  /** `pos_price_list_composition_products.id` del componente venduto. */
  composition_product_id: z.string().uuid(),
  /** Modalità (solo composizioni bundle); null = composizione device_promo non finanziata. */
  payment_mode: PosOfferPaymentModeSchema.nullable(),
  financing_partner: PosOfferFinancingPartnerSchema.nullable(),
  device_advance_amount: z.number().nullable(),
  financed_amount: z.number().nullable(),
  installments_count: z.number().int().nullable(),
  installment_amount: z.number().nullable(),
  canvas: PosOfferCanvasSchema.nullable(),
});
export type PosCompositionOffer = z.infer<typeof PosCompositionOfferSchema>;

/** Tariffa canvass pura (listino canvas, una offerta per listino). */
export const PosCanvasOfferSchema = PosOfferBaseSchema.extend({
  kind: z.literal('canvas'),
  canvas: PosOfferCanvasSchema,
});
export type PosCanvasOffer = z.infer<typeof PosCanvasOfferSchema>;

export const PosProductOfferSchema = z.discriminatedUnion('kind', [
  PosPlainOfferSchema,
  PosCompositionOfferSchema,
  PosCanvasOfferSchema,
]);
export type PosProductOffer = z.infer<typeof PosProductOfferSchema>;

/** Risposta di `GET /pricelists/products/:productId`. */
export const ProductOffersResponseSchema = z.object({
  offers: z.array(PosProductOfferSchema),
});
export type ProductOffersResponse = z.infer<typeof ProductOffersResponseSchema>;

/** Chiave identità riga carrello: prodotto + offerta (composizione, o listino). */
export function posOfferIdentityKey(o: Pick<PosProductOffer, 'kind' | 'price_list_id'> & {
  composition_product_id?: string | null;
}): string {
  if (o.kind === 'composition') return `comp:${o.composition_product_id}`;
  return o.price_list_id;
}

/** TRUE se l'offerta porta una quota finanziata (composizione bundle con modalità). */
export function posOfferHasCredit(o: PosProductOffer): boolean {
  if (o.kind !== 'composition') return false;
  return o.payment_mode != null && (o.financed_amount ?? 0) > 0;
}

// ─── Anteprima pagamenti (split quote) ────────────────────────────────────────

export const PosPaymentPreviewItemSchema = z.object({
  product_id: z.string().uuid(),
  quantity: z.number().positive(),
  /** Totale riga LORDO calcolato in cassa (riveduto server-side per le composizioni). */
  line_total: z.number().min(0),
  price_list_id: z.string().uuid().optional(),
  composition_product_id: z.string().uuid().optional(),
});
export type PosPaymentPreviewItem = z.infer<typeof PosPaymentPreviewItemSchema>;

/** Battuta manuale (tastierino): concorre al totale documento, mai finanziata. */
export const PosPaymentPreviewManualItemSchema = z.object({
  quantity: z.number().positive(),
  /** Totale riga LORDO pre-sconto testata. */
  line_total: z.number().min(0),
});
export type PosPaymentPreviewManualItem = z.infer<typeof PosPaymentPreviewManualItemSchema>;

export const PosPaymentPreviewRequestSchema = z.object({
  store_id: z.string().uuid().optional(),
  customer_id: z.string().uuid().optional(),
  /** Sconto di testata: applicato UNA volta dal server sull'intero documento. */
  discount_percent: z.number().min(0).max(100).optional(),
  /** Righe catalogo con totale LORDO pre-sconto testata. */
  items: z.array(PosPaymentPreviewItemSchema),
  manual_items: z.array(PosPaymentPreviewManualItemSchema).optional(),
});
export type PosPaymentPreviewRequest = z.infer<typeof PosPaymentPreviewRequestSchema>;

/** Quota finanziata di una riga, già assegnata al metodo derivato dalla modalità. */
export const PosFinancedQuotaSchema = z.object({
  composition_product_id: z.string().uuid(),
  product_id: z.string().uuid(),
  payment_mode_id: z.string().uuid(),
  payment_mode_name: z.string(),
  credit_holder: z.enum(PAYMENT_MODE_CREDIT_HOLDERS),
  financing_partner_id: z.string().uuid().nullable(),
  financing_partner_name: z.string().nullable(),
  /** Quota finanziata totale della riga (finanziato × quantità). */
  amount: z.number(),
  installments_count: z.number().int().nullable(),
  installment_amount: z.number().nullable(),
  /** Metodo di incasso del tenant derivato dai codici RT/fattura della modalità. */
  payment_method_id: z.string().uuid(),
  payment_method_name: z.string(),
  /** Task #4334 — codici effettivi del metodo risolto (congelati nello snapshot vendita). */
  payment_method_ade_code: z.number().int().nullable(),
  payment_method_mp_code: z.string().nullable(),
  /** Task #4334 — provenienza: abbinamento esplicito del tenant o codici default della modalità. */
  payment_method_source: z.enum(['explicit', 'codes']),
  /** Etichetta pronta: "quota finanziata — <ente|insegna>". */
  label: z.string(),
});
export type PosFinancedQuota = z.infer<typeof PosFinancedQuotaSchema>;

export const PosEntryFeePreviewSchema = z.object({
  product_id: z.string().uuid(),
  label: z.string(),
  amount: z.number(),
});
export type PosEntryFeePreview = z.infer<typeof PosEntryFeePreviewSchema>;

export const PosPaymentPreviewResponseSchema = z.object({
  /** Totale documento = quote fisiche + quote finanziate (incluse entry fee sintetiche). */
  document_total: z.number(),
  /** Quote fisiche da incassare con metodi scelti dall'operatore. */
  physical_total: z.number(),
  /** Somma delle quote finanziate (metodo derivato). */
  financed_total: z.number(),
  financed_quotas: z.array(PosFinancedQuotaSchema),
  /** Entry fee canvass generate server-side (non presenti nel carrello client). */
  entry_fees: z.array(PosEntryFeePreviewSchema),
});
export type PosPaymentPreviewResponse = z.infer<typeof PosPaymentPreviewResponseSchema>;

/** Codice pratica (CIR) digitato in cassa per una riga con credito ereditato. */
export const SaleInheritedCreditInputSchema = z.object({
  composition_product_id: z.string().uuid(),
  cir: z.string().trim().min(1).max(120),
});
export type SaleInheritedCreditInput = z.infer<typeof SaleInheritedCreditInputSchema>;

/** Codici errore tipizzati del checkout con quote finanziate. */
export const POS_FINANCED_QUOTA_ERROR_CODES = [
  'POS_FINANCED_QUOTA_METHOD_MISSING',
  'POS_FINANCED_QUOTA_METHOD_AMBIGUOUS',
  'POS_FINANCED_QUOTA_EXCEEDS_LINE',
  'POS_FINANCED_QUOTA_PAYMENTS_MISMATCH',
  'POS_INHERITED_CREDIT_CIR_REQUIRED',
] as const;
export type PosFinancedQuotaErrorCode = (typeof POS_FINANCED_QUOTA_ERROR_CODES)[number];
